import { Profile, Links } from '@/lib/types'
import { Button, Badge } from '@/ui'
import { useTranslation } from '@/i18n'

interface EngineerHeroProps {
  profile: Profile
  links: Links
}

export function EngineerHero({ profile, links }: EngineerHeroProps) {
  const { t } = useTranslation()

  return (
    <section className="py-12 px-4 bg-gray-50 border-b-2 border-gray-200 dark:bg-gray-800 dark:border-gray-700">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start gap-8">
          {/* Avatar */}
          <div className="flex-shrink-0">
            {profile.avatar ? (
              <img
                src={profile.avatar}
                alt={profile.full_name}
                className="w-24 h-24 rounded-lg object-cover border-2 border-gray-300 dark:border-gray-600"
              />
            ) : (
              <div className="w-24 h-24 rounded-lg bg-gray-300 flex items-center justify-center text-3xl font-bold text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                {profile.full_name.charAt(0)}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1">
            <h1 className="text-4xl font-bold mb-2 dark:text-gray-100">{profile.full_name}</h1>
            <p className="text-xl text-gray-600 mb-2 dark:text-gray-400">{profile.headline}</p>
            <p className="text-base text-gray-700 mb-4 dark:text-gray-300">
              {profile.summary_short}
            </p>

            {/* Tech Stack Badges */}
            <div className="flex flex-wrap gap-2 mb-6">
              {profile.keywords.map((keyword, i) => (
                <Badge key={i} variant="primary" size="md">
                  {keyword}
                </Badge>
              ))}
            </div>

            {/* Metrics */}
            <div className="flex gap-6 text-sm text-gray-600 dark:text-gray-400 mb-6">
              <div>
                <span className="font-bold text-gray-900 dark:text-gray-100">
                  {profile.languages.length}
                </span>{' '}
                {t('hero.languages')}
              </div>
              <div>
                <span className="font-bold text-gray-900 dark:text-gray-100">
                  {profile.location}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="primary" href={links.github}>
                {t('hero.github')}
              </Button>
              <Button variant="outline" href={`mailto:${links.email}`}>
                {t('hero.email')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
